import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/** @returns {string} The ids of every cart section on the page, so they all re-render. */
function cartSectionIds() {
  const ids = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  return [...new Set(ids)].join(',');
}

/** @returns {Promise<{ item_count?: number, discount_codes?: { code: string }[], attributes?: Record<string, string> } | undefined>} */
async function fetchCart() {
  try {
    return await (await fetch(`${Theme.routes.cart_url}.js`)).json();
  } catch (_) {
    return undefined;
  }
}

/**
 * POSTs to /cart/update.js.
 * @param {Record<string, unknown>} payload
 */
async function updateCart(payload) {
  const response = await fetch(`${Theme.routes.cart_update_url}.js`, fetchConfig('json', { body: JSON.stringify(payload) }));
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not update the bag');
  return data;
}

/**
 * Saves the gift size on the cart and adds the gift with a hidden `_free_gift` line property,
 * using the theme's native flow (POST cart_add_url with the cart section ids, then dispatch
 * CartAddEvent) so the drawer re-renders with the gift as a line item. `_free_gift_added`
 * marks it as added once, so a gift the customer removes isn't put straight back.
 * The £0.00 price comes from the discount code, not from here.
 * @param {string} variantId
 */
async function addGift(variantId) {
  await updateCart({ attributes: { _free_gift_variant: variantId, _free_gift_added: '1' } });

  const body = JSON.stringify({
    items: [{ id: Number(variantId), quantity: 1, properties: { _free_gift: 'true' } }],
    sections: cartSectionIds(),
  });
  const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add your gift');

  const cart = await fetchCart();
  document.dispatchEvent(
    new CartAddEvent(cart, 'cart-free-gift', {
      source: 'cart-free-gift',
      itemCount: cart?.item_count,
      sections: data.sections,
      variantId,
    })
  );
}

/**
 * A size picker: radios named `free_gift_variant` and a submit button that unlocks once a
 * size is chosen, using `data-label-ready` as its new label.
 */
class FreeGiftPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('submit', this.#onSubmit);
  }

  get button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('button[type="submit"]'));
  }

  get selected() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="free_gift_variant"]:checked'));
  }

  #onChange = () => {
    const button = this.button;
    if (!button) return;
    button.disabled = false;
    button.textContent = button.dataset.labelReady || button.textContent;
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();

    const button = this.button;
    const checked = this.selected;
    if (!button || !checked || button.disabled) return;

    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-error]'));
    if (error) error.hidden = true;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      await this.claim(checked.value);
    } catch (err) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (error) {
        error.textContent = err instanceof Error ? err.message : String(err);
        error.hidden = false;
      }
    }
  };

  /** @param {string} variantId */
  async claim(variantId) {
    await addGift(variantId);
  }
}

/** The picker in the cart drawer and on the cart page, shown when no size has been saved. */
class CartFreeGiftComponent extends FreeGiftPicker {}

/**
 * The picker on the /pages/free-socks landing page. Saves the size on the cart so the gift is
 * added with the first paid item, adds it straight away if the cart already has one, then
 * sends the customer off to shop.
 */
class FreeGiftLanding extends FreeGiftPicker {
  #code = '';

  connectedCallback() {
    super.connectedCallback();
    this.#checkCode();
  }

  /**
   * Liquid can't read the cart's discount codes, so look for the subscriber's code in /cart.js,
   * copy it into the `_free_gift_code` attribute the theme reads, and show the matching panel.
   */
  async #checkCode() {
    const prefix = (this.dataset.prefix || '').trim().toUpperCase();
    const cart = await fetchCart();
    if (!cart) return;

    /** @type {{ code: string }[]} */
    const codes = cart.discount_codes || [];
    const code = (prefix && codes.find((c) => c.code.toUpperCase().startsWith(prefix))?.code) || '';
    const saved = cart.attributes?._free_gift_code || '';
    this.#code = code;

    // "Offer to everyone" (password-protected store) needs no code.
    const eligible = Boolean(code) || this.hasAttribute('data-open');
    for (const panel of this.querySelectorAll('[data-free-gift-panel]')) {
      if (panel instanceof HTMLElement) panel.hidden = panel.dataset.freeGiftPanel !== (eligible ? 'eligible' : 'ineligible');
    }

    if (code !== saved) {
      try {
        await updateCart({ attributes: { _free_gift_code: code } });
      } catch (_) {
        /* The drawer won't offer the gift until the code is saved; the next visit retries. */
      }
    }
  }

  /** @param {string} variantId */
  async claim(variantId) {
    const added = this.dataset.hasPaidItems === 'true';
    await updateCart({ attributes: { _free_gift_code: this.#code, _free_gift_variant: variantId, _free_gift_added: '' } });
    if (added) await addGift(variantId);

    try {
      sessionStorage.setItem(
        REMINDER_KEY,
        JSON.stringify({
          size: this.selected?.nextElementSibling?.textContent?.trim() || '',
          image: this.dataset.image || '',
          title: this.dataset.title || '',
          added,
        })
      );
    } catch (_) {
      /* No reminder, but the gift is still saved on the cart. */
    }

    window.location.href = this.dataset.redirect || Theme.routes.root_url || '/';
  }
}

const REMINDER_KEY = 'antidote:free-gift-claimed';

/**
 * After claiming on the landing page, a small pop-up on the next page reminds the customer
 * their socks are saved. Shown once, closes itself after a few seconds.
 */
function showClaimReminder() {
  /** @type {{ size?: string, image?: string, title?: string, added?: boolean } | null} */
  let claim = null;
  try {
    claim = JSON.parse(sessionStorage.getItem(REMINDER_KEY) || 'null');
    sessionStorage.removeItem(REMINDER_KEY);
  } catch (_) {
    return;
  }
  if (!claim) return;

  const toast = document.createElement('div');
  toast.className = 'free-gift-toast';
  toast.setAttribute('role', 'status');

  if (claim.image) {
    const img = document.createElement('img');
    img.className = 'free-gift-toast__img';
    img.src = claim.image;
    img.alt = '';
    toast.append(img);
  }

  const body = document.createElement('div');
  body.className = 'free-gift-toast__body';
  const title = document.createElement('p');
  title.className = 'free-gift-toast__title';
  title.textContent = claim.added ? 'Your free socks are in your bag' : 'Your free socks are saved';
  const text = document.createElement('p');
  text.className = 'free-gift-toast__text';
  const size = claim.size ? `Size ${claim.size}. ` : '';
  text.textContent = claim.added
    ? `${size}They're on us with this order.`
    : `${size}They'll be added to your bag, free, with your first order.`;
  body.append(title, text);

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'free-gift-toast__close button-unstyled';
  close.setAttribute('aria-label', 'Close');
  close.textContent = '×';

  toast.append(body, close);
  document.body.append(toast);

  const dismiss = () => {
    toast.classList.remove('is-visible');
    setTimeout(() => toast.remove(), 400);
  };
  close.addEventListener('click', dismiss);
  requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('is-visible')));
  setTimeout(dismiss, 8000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', showClaimReminder, { once: true });
} else {
  showClaimReminder();
}

/** Line keys already being fixed, so the drawer and cart page don't both send a request. */
const pending = new Set();

/**
 * Rendered by snippets/cart-free-gift.liquid only when the cart needs fixing:
 * - `add`: a paid item is in and a size was saved, so add the gift.
 * - `remove`: nothing paid is left (or the code has gone), so remove the gift and let it be
 *   re-added with the next paid item.
 * - `trim`: more than one gift, so set it back to one.
 * - `revoke`: the gift is beside a paid item but not discounted, so the code isn't valid for
 *   this cart; remove it and withdraw the offer rather than charge full price for it.
 */
class CartFreeGiftGuard extends HTMLElement {
  connectedCallback() {
    const { action, lineKey, variantId } = this.dataset;
    const key = lineKey || `add:${variantId}`;
    if (!action || pending.has(key)) return;

    pending.add(key);
    this.#run(action, lineKey, variantId)
      .catch(() => {
        /* If this fails the gift stays as it is; the discount still only prices one pair at £0.00, and only beside a paid item. */
      })
      .finally(() => pending.delete(key));
  }

  /**
   * @param {string} action
   * @param {string | undefined} lineKey
   * @param {string | undefined} variantId
   */
  async #run(action, lineKey, variantId) {
    if (action === 'add' && variantId) return addGift(variantId);
    if (!lineKey) return;

    /** @type {Record<string, Record<string, unknown>>} */
    const payloads = {
      remove: { updates: { [lineKey]: 0 }, attributes: { _free_gift_added: '' } },
      revoke: { updates: { [lineKey]: 0 }, attributes: { _free_gift_code: '', _free_gift_added: '' } },
      trim: { updates: { [lineKey]: 1 } },
    };
    const payload = payloads[action];
    if (!payload) return;

    const data = await updateCart({ ...payload, sections: cartSectionIds() });
    document.dispatchEvent(
      new CartUpdateEvent(data, 'cart-free-gift', {
        source: 'cart-free-gift',
        itemCount: data.item_count,
        sections: data.sections,
      })
    );
  }
}

if (!customElements.get('cart-free-gift-component')) {
  customElements.define('cart-free-gift-component', CartFreeGiftComponent);
}

if (!customElements.get('free-gift-landing')) {
  customElements.define('free-gift-landing', FreeGiftLanding);
}

if (!customElements.get('cart-free-gift-guard')) {
  customElements.define('cart-free-gift-guard', CartFreeGiftGuard);
}
